import { TextArea } from "semantic-ui-react";
import React, { useEffect, useState } from "react";
import { useSemanticUiTextAreaRef } from "utilities/hookutils";
import { PhosphorBracketsCurlyButton } from "components/Atoms/PhosphorIconButton";
import SelectMergeFieldModal from "components/MergeField/SelectMergeFieldModal/SelectMergeFieldModal";

interface Props {
  focusOnMount?: boolean
  maxLength: number
  value: string
  onChange: (newValue: string) => void
  onCtrlEnter?: () => void
  onEsc?: () => void
  sectionName: string
  availableMergeFieldTopics: server.dto.AvailableMergeFieldTopic[]
  rows: number
}

const OptionalTextAreaInputWithMergeField = (props: Props) => {
  const [isMergeFieldModalOpen, setIsMergeFieldModalOpen] = useState(false);
  const [selectionStart, setSelectionStart] = useState<number>(null);
  const [selectionEnd, setSelectionEnd] = useState<number>(null);
  const textAreaRef = useSemanticUiTextAreaRef();

  useEffect(() => {
    if (props.focusOnMount) {
      textAreaRef.ref.current.focus();
      textAreaRef.ref.current.selectionStart = textAreaRef.ref.current.selectionEnd - textAreaRef.ref.current.value.length;
      textAreaRef.ref.current.scrollTop = textAreaRef.ref.current.scrollHeight;
    }
  }, []);

  const onChange = (_, { value }) => {
    props.onChange(value);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      props.onEsc?.();
    } else if (e.ctrlKey && e.key === "Enter") {
      props.onCtrlEnter?.();
    }
  }

  const onMergeFieldSelected = (mergeField: string) => {
    const newValue = props.value.substring(0, selectionStart) + mergeField + props.value.substring(selectionEnd ?? selectionStart);
    props.onChange(newValue);
    setIsMergeFieldModalOpen(false);
    setTimeout(() => {
      const cursorPosition = selectionStart + mergeField.length;
      textAreaRef.ref.current.setSelectionRange(cursorPosition, cursorPosition);
      textAreaRef.ref.current.focus();
    }, 1);
  }

  const onSelectMergeFieldModalClose = () => {
    setIsMergeFieldModalOpen(false);
    textAreaRef.ref.current.focus();
  }

  const rows = props.rows ?? 6;

  return <div className="flex gap">
    <TextArea
      style={{ lineHeight: `${16}px` }}
      rows={rows}
      ref={textAreaRef.refSetter}
      value={props.value}
      onChange={onChange}
      maxLength={props.maxLength}
      onKeyDown={onKeyDown}
      onKeyUp={e => {
        setSelectionStart(e.target.selectionStart);
        setSelectionEnd(e.target.selectionEnd);
      }}
      onClick={e => {
        setSelectionStart(e.target.selectionStart);
        setSelectionEnd(e.target.selectionEnd);
      }}
      fluid="true"
    />
    <PhosphorBracketsCurlyButton
      layout="in-square"
      selectedStyle="green-bg"
      onClick={() => setIsMergeFieldModalOpen(true)}
      iconSize="large"
    />
    <SelectMergeFieldModal
      sectionName={props.sectionName}
      availableMergeFieldTopics={props.availableMergeFieldTopics}
      isOpen={isMergeFieldModalOpen}
      onClose={onSelectMergeFieldModalClose}
      onSelect={onMergeFieldSelected}
    />
  </div>;
}

export default OptionalTextAreaInputWithMergeField;